import {
  ADVISORY_COMPONENT_IDS,
  COMPONENT_SCHEMAS,
  COMPOSITE_DEFINITIONS,
  LAYOUT_PRESETS,
  QUESTION_DEFINITIONS,
  getCompositeSectionDescriptors,
  validateCompositeAnswerV01,
  type AdvisoryEngagementId,
  type AdvisoryQuestionId,
  type CompositeAnswerV01,
  type LayoutPreset,
  type SectionRole,
  type ValidationIssue,
} from './index';

export interface LiveCompositeSectionV01 {
  componentId: string;
  title: string;
  role: SectionRole;
  filledFrom: string;
  fields: string[];
  data: Record<string, unknown>;
}

export interface LiveOmittedCompositeSectionV01 {
  componentId: string;
  title: string;
  role: SectionRole;
  reason: string;
}

export interface LiveCompositeSlideV01 {
  layout: LayoutPreset;
  sections: string[];
}

export interface LiveCompositeAnswerV01 {
  compositeId: string;
  version: 1;
  filledBy: string;
  primaryFilled: boolean;
  sections: LiveCompositeSectionV01[];
  omitted: LiveOmittedCompositeSectionV01[];
  slidePlans: { short: LiveCompositeSlideV01[]; long: LiveCompositeSlideV01[] };
}

export interface LiveRenderingIdentityV01 {
  engagementId: AdvisoryEngagementId;
  questionId: AdvisoryQuestionId;
  /** The backend-provided project catalogue is authoritative for cross-engagement additions. */
  allowedQuestionIds: readonly AdvisoryQuestionId[];
}

export type LiveRenderingValidationResult =
  | { ok: true; value: LiveCompositeAnswerV01 }
  | { ok: false; issues: readonly ValidationIssue[] };

const record = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
const issue = (path: string, code: string, message: string): ValidationIssue => ({
  path,
  code,
  message,
});
const stringValue = (value: unknown): string => (typeof value === 'string' ? value : '');
const exactKeys = (value: Record<string, unknown>, keys: readonly string[]): boolean => {
  const actual = Object.keys(value);
  return actual.length === keys.length && actual.every(key => keys.includes(key));
};
const isRole = (value: unknown): value is SectionRole =>
  value === 'primary' || value === 'optional' || value === 'slide';
const isLayout = (value: unknown): value is LayoutPreset =>
  typeof value === 'string' && (LAYOUT_PRESETS as readonly string[]).includes(value);
const isKnownComponent = (value: string): value is (typeof ADVISORY_COMPONENT_IDS)[number] =>
  (ADVISORY_COMPONENT_IDS as readonly string[]).includes(value);

function validateGenericField(value: unknown): boolean {
  if (typeof value === 'string') return true;
  if (!Array.isArray(value)) return false;
  if (value.every(item => typeof item === 'string')) return true;
  return value.every(item => {
    const row = record(item);
    return (
      row !== null &&
      Object.keys(row).length > 0 &&
      Object.values(row).every(cell => cell === null || ['string', 'number'].includes(typeof cell))
    );
  });
}

function validateKnownSection(
  section: LiveCompositeSectionV01,
  path: string,
  issues: ValidationIssue[]
): void {
  if (!isKnownComponent(section.componentId)) return;
  const schema = COMPONENT_SCHEMAS[section.componentId];
  const byName = new Map(schema.attributes.map(attribute => [attribute.name, attribute]));
  section.fields.forEach(field => {
    const attribute = byName.get(field);
    if (!attribute) {
      issues.push(issue(`${path}/fields`, 'component.unknown_field', `${field} is not declared`));
      return;
    }
    const value = section.data[field];
    if (attribute.type === 'rows') {
      if (
        !Array.isArray(value) ||
        !value.every(rowValue => {
          const row = record(rowValue);
          return (
            row !== null &&
            exactKeys(row, attribute.columns ?? []) &&
            Object.values(row).every(cell => typeof cell === 'string')
          );
        })
      ) {
        issues.push(
          issue(
            `${path}/data/${field}`,
            'component.malformed_rows',
            'Expected exact string columns'
          )
        );
      }
    } else if (attribute.type === 'list') {
      if (!Array.isArray(value) || !value.every(item => typeof item === 'string')) {
        issues.push(
          issue(`${path}/data/${field}`, 'component.malformed_list', 'Expected a string list')
        );
      }
    } else if (attribute.type === 'enum') {
      if (typeof value !== 'string' || !(attribute.options ?? []).includes(value)) {
        issues.push(
          issue(`${path}/data/${field}`, 'component.malformed_enum', 'Unsupported enum value')
        );
      }
    } else if (attribute.type === 'currency') {
      const currency = record(value);
      if (
        !currency ||
        !exactKeys(currency, ['amount', 'currency', 'unit']) ||
        typeof currency.amount !== 'number' ||
        typeof currency.currency !== 'string' ||
        !(typeof currency.unit === 'string' || currency.unit === null)
      ) {
        issues.push(
          issue(
            `${path}/data/${field}`,
            'component.malformed_currency',
            'Expected a currency value'
          )
        );
      }
    } else if (typeof value !== 'string') {
      issues.push(issue(`${path}/data/${field}`, 'component.malformed_text', 'Expected a string'));
    }
  });
  schema.attributes
    .filter(attribute => attribute.required)
    .forEach(attribute => {
      if (!section.fields.includes(attribute.name)) {
        issues.push(
          issue(
            `${path}/fields`,
            'component.required_field_missing',
            `${attribute.name} is required`
          )
        );
      }
    });
}

/**
 * Live envelope validation keeps the checked-in v0.1 validator untouched.
 * Exact catalogue payloads take that strict path; only an explicitly unknown
 * component uses the generic fallback.
 */
export function validateLiveRenderingEnvelopeV01(
  input: unknown,
  identity: LiveRenderingIdentityV01
): LiveRenderingValidationResult {
  if (!identity.allowedQuestionIds.includes(identity.questionId)) {
    return {
      ok: false,
      issues: [
        issue(
          '/questionId',
          'identity.question_not_in_project',
          'Question is not in this project catalogue'
        ),
      ],
    };
  }
  const question = QUESTION_DEFINITIONS[identity.questionId];
  if (!question) {
    return {
      ok: false,
      issues: [issue('/questionId', 'identity.unknown_question', 'Unknown question')],
    };
  }

  const strict = validateCompositeAnswerV01(input, identity);
  if (strict.ok) return { ok: true, value: strict.value as unknown as LiveCompositeAnswerV01 };
  const strictWithoutEngagement = strict.issues.filter(
    candidate => candidate.code !== 'identity.question_engagement_mismatch'
  );
  if (strictWithoutEngagement.length === 0) {
    return { ok: true, value: input as CompositeAnswerV01 as unknown as LiveCompositeAnswerV01 };
  }

  const root = record(input);
  if (!root) return { ok: false, issues: strict.issues };
  const rawSections = Array.isArray(root.sections) ? root.sections : [];
  const hasUnknownComponent = rawSections.some(sectionValue => {
    const section = record(sectionValue);
    return (
      section && typeof section.componentId === 'string' && !isKnownComponent(section.componentId)
    );
  });
  if (!hasUnknownComponent) return { ok: false, issues: strictWithoutEngagement };

  const issues: ValidationIssue[] = [];
  if (
    !exactKeys(root, [
      'compositeId',
      'version',
      'filledBy',
      'primaryFilled',
      'sections',
      'omitted',
      'slidePlans',
    ])
  ) {
    issues.push(issue('', 'schema.additionalProperties', 'Envelope properties do not match v0.1'));
  }
  if (root.version !== 1)
    issues.push(issue('/version', 'schema.const', 'Only version 1 is supported'));
  if (typeof root.compositeId !== 'string' || !root.compositeId) {
    issues.push(issue('/compositeId', 'schema.type', 'compositeId is required'));
  } else if (root.compositeId !== question.compositeId) {
    issues.push(
      issue(
        '/compositeId',
        'identity.question_composite_mismatch',
        `${identity.questionId} must use ${question.compositeId}`
      )
    );
  }
  if (typeof root.filledBy !== 'string' || typeof root.primaryFilled !== 'boolean') {
    issues.push(issue('', 'schema.type', 'filledBy and primaryFilled are required'));
  }

  const sections: LiveCompositeSectionV01[] = [];
  const seen = new Set<string>();
  rawSections.forEach((sectionValue, index) => {
    const path = `/sections/${index}`;
    const section = record(sectionValue);
    if (
      !section ||
      !exactKeys(section, ['componentId', 'title', 'role', 'filledFrom', 'fields', 'data'])
    ) {
      issues.push(issue(path, 'schema.section', 'Malformed section'));
      return;
    }
    const componentId = typeof section.componentId === 'string' ? section.componentId : '';
    const fields = Array.isArray(section.fields)
      ? section.fields.filter((field): field is string => typeof field === 'string')
      : [];
    const data = record(section.data);
    if (
      !componentId ||
      !isRole(section.role) ||
      !data ||
      fields.length !== (section.fields as unknown[]).length
    ) {
      issues.push(issue(path, 'schema.section', 'Malformed section identity or data'));
      return;
    }
    if (seen.has(componentId))
      issues.push(issue(`${path}/componentId`, 'component.duplicate', 'Duplicate component'));
    seen.add(componentId);
    if (
      fields.length !== Object.keys(data).length ||
      !fields.every(field => Object.prototype.hasOwnProperty.call(data, field))
    ) {
      issues.push(
        issue(`${path}/fields`, 'component.fields_data_mismatch', 'fields must index data exactly')
      );
    }
    const mapped: LiveCompositeSectionV01 = {
      componentId,
      title: stringValue(section.title),
      role: section.role,
      filledFrom: stringValue(section.filledFrom),
      fields,
      data,
    };
    if (isKnownComponent(componentId)) {
      validateKnownSection(mapped, path, issues);
    } else {
      fields.forEach(field => {
        if (!validateGenericField(data[field])) {
          issues.push(
            issue(
              `${path}/data/${field}`,
              'component.malformed_generic',
              'Unsupported generic value'
            )
          );
        }
      });
    }
    sections.push(mapped);
  });

  const omittedValues = Array.isArray(root.omitted) ? root.omitted : [];
  const omitted: LiveOmittedCompositeSectionV01[] = [];
  omittedValues.forEach((value, index) => {
    const entry = record(value);
    if (
      !entry ||
      !exactKeys(entry, ['componentId', 'title', 'role', 'reason']) ||
      typeof entry.componentId !== 'string' ||
      !isRole(entry.role) ||
      typeof entry.title !== 'string' ||
      typeof entry.reason !== 'string'
    ) {
      issues.push(issue(`/omitted/${index}`, 'schema.omitted', 'Malformed omission'));
      return;
    }
    if (seen.has(entry.componentId))
      issues.push(issue(`/omitted/${index}`, 'component.duplicate', 'Duplicate component'));
    seen.add(entry.componentId);
    omitted.push(entry as unknown as LiveOmittedCompositeSectionV01);
  });

  const composite = COMPOSITE_DEFINITIONS[question.compositeId];
  const descriptors = getCompositeSectionDescriptors(composite);
  const descriptorById = new Map(
    descriptors.map(descriptor => [descriptor.componentId, descriptor])
  );
  sections.forEach((section, index) => {
    if (!isKnownComponent(section.componentId)) return;
    const descriptor = descriptorById.get(section.componentId);
    if (!descriptor) {
      issues.push(
        issue(
          `/sections/${index}/componentId`,
          'component.unknown',
          `${section.componentId} does not belong to ${question.compositeId}`
        )
      );
    } else if (section.role !== descriptor.role) {
      issues.push(
        issue(
          `/sections/${index}/role`,
          'component.role_mismatch',
          `${section.componentId} has the wrong role`
        )
      );
    }
  });
  omitted.forEach((section, index) => {
    if (!isKnownComponent(section.componentId)) return;
    const descriptor = descriptorById.get(section.componentId);
    if (!descriptor || section.role !== descriptor.role) {
      issues.push(
        issue(
          `/omitted/${index}/role`,
          'component.role_mismatch',
          'Known omission has the wrong role'
        )
      );
    }
  });
  descriptors.forEach(descriptor => {
    if (!seen.has(descriptor.componentId)) {
      issues.push(
        issue('/sections', 'component.unaccounted', `${descriptor.componentId} is unaccounted`)
      );
    }
  });
  const knownPrimary = composite.primarySection.componentId;
  const primaryIndex = sections.findIndex(section => section.componentId === knownPrimary);
  const primaryIsFilled = primaryIndex >= 0;
  if (root.primaryFilled !== primaryIsFilled) {
    issues.push(
      issue('/primaryFilled', 'component.primary_filled_mismatch', 'primaryFilled is inconsistent')
    );
  }
  if (primaryIndex > 0)
    issues.push(
      issue(`/sections/${primaryIndex}`, 'component.primary_not_first', 'Primary must be first')
    );

  const slidePlansRecord = record(root.slidePlans);
  const parseSlides = (value: unknown, path: string): LiveCompositeSlideV01[] => {
    if (!Array.isArray(value)) {
      issues.push(issue(path, 'schema.type', 'Expected slides'));
      return [];
    }
    return value.flatMap((slideValue, index) => {
      const slide = record(slideValue);
      if (
        !slide ||
        !exactKeys(slide, ['layout', 'sections']) ||
        !isLayout(slide.layout) ||
        !Array.isArray(slide.sections)
      ) {
        issues.push(issue(`${path}/${index}`, 'schema.slide', 'Malformed slide'));
        return [];
      }
      const componentIds = slide.sections.filter((id): id is string => typeof id === 'string');
      if (componentIds.length !== slide.sections.length || componentIds.some(id => !seen.has(id))) {
        issues.push(
          issue(
            `${path}/${index}/sections`,
            'layout.unresolved_component',
            'Slide references an unknown section'
          )
        );
      }
      return [{ layout: slide.layout, sections: componentIds }];
    });
  };
  const slidePlans = {
    short: parseSlides(slidePlansRecord?.short, '/slidePlans/short'),
    long: parseSlides(slidePlansRecord?.long, '/slidePlans/long'),
  };
  const knownDescriptorIds = new Set(descriptors.map(descriptor => descriptor.componentId));
  const knownProjection = (slides: readonly LiveCompositeSlideV01[]) =>
    slides
      .map(slide => ({
        layout: slide.layout,
        sections: slide.sections.filter(section => knownDescriptorIds.has(section as never)),
      }))
      .filter(slide => slide.sections.length > 0);
  if (
    JSON.stringify({
      short: knownProjection(slidePlans.short),
      long: knownProjection(slidePlans.long),
    }) !== JSON.stringify(composite.slidePlans)
  ) {
    issues.push(issue('/slidePlans', 'layout.plan_mismatch', 'Known slide plan changed'));
  }
  if (issues.length > 0) return { ok: false, issues };
  return {
    ok: true,
    value: {
      compositeId: root.compositeId as string,
      version: 1,
      filledBy: root.filledBy as string,
      primaryFilled: root.primaryFilled as boolean,
      sections,
      omitted,
      slidePlans,
    },
  };
}
